import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import "./index.css";
import "./App.css";
import { RouterProvider } from "react-router-dom";
import router from "@/config/route-main-config.tsx";
import routerGuest from "@/config/route-guest-config";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import { persister, queryClient } from "@/hooks/use-query-client";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { GroupProvider } from "./context/group-provider";
import { ErrorProvider } from "./context/error-provider";
import { ErrorNotificationContainer } from "./pages/error-notification-container";
import { Provider } from "react-redux";
import { presistor, store } from "./store/store";
import { PersistGate } from "redux-persist/integration/react";
import { initializeProductConfig } from "./config/product-config";
import { ThemeProvider } from "./context/theme-provider";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider } from "./context/auth-provider";
import { LoggerProvider } from "./services/logger/logger-provider";
import { listenForParentSessionClear } from "@/lib/parent-session-bridge";
import { QueryKeys } from "@/constants";

listenForParentSessionClear();

const setupConfig = () => {
  const urlParams = new URLSearchParams(window.location.search);
  if (window.location.pathname === "/guest-login") {
    const redirectKey = urlParams.get("redirectKey");
    window.sessionStorage.setItem("redirectKey", redirectKey || "");
    return routerGuest;
  } else if (window.sessionStorage.getItem("redirectKey")) {
    return routerGuest;
  } else {
    return router;
  }
};

initializeProductConfig().then((theme) => {
  // console.log("THEME productConfig initialized");
  const root = createRoot(document.getElementById("root")!);
  // console.log("THEME Product Config", (window as any).__PRODUCT_CONFIG__);
  // console.log("THEME", theme);
  root.render(
    <StrictMode>
      {/* //PersistQueryClientProvider is used to persist the query in storage */}
      {/* //QueryClientProvider is used to persist the query in cache memory */}
      {/* <QueryClientProvider client={queryClient}> */}
      <Provider store={store}>
        <PersistGate loading={null} persistor={presistor}>
          <PersistQueryClientProvider
            client={queryClient}
            persistOptions={{
              persister,
              dehydrateOptions: {
                shouldDehydrateQuery: (query) => {
                  const key = query.queryKey[0] as string;
                  return ![
                    "csrChatMembersToken",
                    "csrChatChannels",
                    "csrChannelMessages",
                    // One cost estimate can be thousands of rows, and its
                    // professional batches are as bulky again. This cache
                    // shares sessionStorage's quota with the auth and redux
                    // state, so both are kept in memory only, where React
                    // Query caches them for minutes anyway.
                    QueryKeys.HCBB_ESTIMATE,
                    QueryKeys.HCBB_PROFESSIONALS,
                  ].includes(key);
                },
              },
            }}
          >
            <ThemeProvider appTheme={theme}>
              <TooltipProvider>
                <LoggerProvider>
                  <GroupProvider>
                    <ErrorProvider>
                      <AuthProvider>
                        <RouterProvider router={setupConfig()} />
                        <ErrorNotificationContainer />
                      </AuthProvider>
                    </ErrorProvider>
                  </GroupProvider>
                </LoggerProvider>
              </TooltipProvider>

              {process.env.NODE_ENV === "development" && (
                <ReactQueryDevtools
                  initialIsOpen={false}
                  position="bottom"
                  buttonPosition="bottom-left"
                />
              )}
            </ThemeProvider>
          </PersistQueryClientProvider>
        </PersistGate>
      </Provider>
      {/* </QueryClientProvider> */}
    </StrictMode>,
  );
});
